// Malaysian property-loan & statutory-fee math shared by the Tools calculators
// (MortgagePage, DsrPage). Pure functions — no state, nothing saved.
//
// Scales implemented (verified July 2026):
// • Legal fees — Solicitors' Remuneration Order 2023 (in force 15 Jul 2023):
//   1.25% on the first RM500k (min RM500), 1% on the next RM7m, above RM7.5m
//   negotiable (charged at 1% here). The same scale applies to the SPA
//   (on purchase price) and the loan facility agreement (on loan amount).
//   Quotes in practice add 8% SST + disbursements — excluded here, like the
//   common realtor calculators, and called out in the on-screen disclaimer.
// • MOT/DOA stamp duty — 1% first RM100k, 2% RM100k–500k, 3% RM500k–1m,
//   4% above RM1m (Malaysian citizens/PR).
// • Loan agreement stamp duty — flat 0.5% of the facility amount.
// • First-time home buyer (Budget 2026) — 100% exemption on BOTH the MOT and
//   the loan-agreement stamp duty for residential homes ≤ RM500k, SPA signed
//   1 Jan 2026 – 31 Dec 2027.
// • Valuation fee (subsale bank valuation) — Valuers' Seventh Schedule scale:
//   0.25% first RM100k, 0.2% next RM2m, 0.167% next RM7m, 0.125% next RM15m,
//   0.1% next RM50m; minimum RM400.

export const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));

export const FTB_PRICE_CAP = 500000; // Budget 2026 first-time-buyer exemption cap

// Marginal tiers: rate applies to the slice between the previous cap and upTo.
const slice = (amount, tiers) => {
  let fee = 0;
  let prev = 0;
  for (const t of tiers) {
    if (amount <= prev) break;
    fee += (Math.min(amount, t.upTo) - prev) * t.rate;
    prev = t.upTo;
  }
  return fee;
};

const SRO_2023 = [
  { upTo: 500000, rate: 0.0125 },
  { upTo: 7500000, rate: 0.01 },
  { upTo: Infinity, rate: 0.01 }, // negotiable above RM7.5m — charged at 1%
];

const MOT_STAMP = [
  { upTo: 100000, rate: 0.01 },
  { upTo: 500000, rate: 0.02 },
  { upTo: 1000000, rate: 0.03 },
  { upTo: Infinity, rate: 0.04 },
];

const VALUATION = [
  { upTo: 100000, rate: 0.0025 },
  { upTo: 2100000, rate: 0.002 },
  { upTo: 9100000, rate: 0.00167 },
  { upTo: 24100000, rate: 0.00125 },
  { upTo: 74100000, rate: 0.001 },
  { upTo: Infinity, rate: 0.001 },
];

export const spaLegalFee = (price) => (num(price) > 0 ? Math.max(500, slice(num(price), SRO_2023)) : 0);
export const loanLegalFee = (loan) => (num(loan) > 0 ? Math.max(500, slice(num(loan), SRO_2023)) : 0);
export const motStampDuty = (price) => slice(num(price), MOT_STAMP);
export const loanStampDuty = (loan) => num(loan) * 0.005;
export const valuationFee = (value) => (num(value) > 0 ? Math.max(400, slice(num(value), VALUATION)) : 0);

// Flat per-side estimate for solicitor disbursements & misc charges (search fees,
// registration, printing, SST on fees etc.) — the same RM2,500 estimate the common
// Malaysian mortgage calculators use; always labelled as an estimate on screen.
export const DISBURSEMENT_ESTIMATE = 2500;

// Month-by-month amortisation: [{ month, principal, interest, balance }].
export function amortizationSchedule(principal, annualRatePct, years) {
  const P = num(principal);
  const n = Math.round(num(years) * 12);
  if (P <= 0 || n <= 0) return [];
  const r = num(annualRatePct) / 100 / 12;
  const pay = monthlyInstallment(P, annualRatePct, years);
  const rows = [];
  let bal = P;
  for (let m = 1; m <= n; m++) {
    const interest = bal * r;
    const princ = Math.min(pay - interest, bal);
    bal = Math.max(0, bal - princ);
    rows.push({ month: m, principal: princ, interest, balance: bal });
  }
  return rows;
}

// Standard amortised repayment: P·r / (1 − (1+r)^−n), r = monthly rate.
export function monthlyInstallment(principal, annualRatePct, years) {
  const P = num(principal);
  const n = Math.round(num(years) * 12);
  if (P <= 0 || n <= 0) return 0;
  const r = num(annualRatePct) / 100 / 12;
  if (r === 0) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
}

// Inverse of the above — the loan a given monthly instalment can service.
export function maxLoanFromInstallment(installment, annualRatePct, years) {
  const m = num(installment);
  const n = Math.round(num(years) * 12);
  if (m <= 0 || n <= 0) return 0;
  const r = num(annualRatePct) / 100 / 12;
  if (r === 0) return m * n;
  return (m * (1 - Math.pow(1 + r, -n))) / r;
}

// ─── Mortgage (Pre/New Launch + Subsale) ─────────────────────────────────
// Basis notes:
// • Developer discount (new launch) nets off the SPA price first; the loan
//   margin, legal fees and stamp duty are all computed on the net price —
//   the convention used by Malaysian new-launch mortgage calculators.
// • "Finance Loan Doc & Valuation Costs" (subsale) rolls the loan legal fee,
//   loan stamp duty and valuation fee INTO the facility. Fees then depend on
//   the facility they enlarge, so we fix-point iterate (converges in 2–3
//   rounds — the fee-on-fee effect is ~1.5%).
export function computeMortgage(inp) {
  const mode = inp.mode; // 'NEW' | 'SUBSALE'
  const price = num(inp.price);
  const discountPct = mode === 'NEW' ? num(inp.discountPct) : 0;
  const netPrice = price * (1 - discountPct / 100);

  const down =
    inp.downMode === 'RM'
      ? Math.min(num(inp.downValue), netPrice)
      : netPrice * (Math.min(num(inp.downValue), 100) / 100);

  const otherFinanced = mode === 'SUBSALE' ? num(inp.otherFinancedFees) : 0;
  const baseLoan = Math.max(0, netPrice - down) + otherFinanced;

  const ftbEligible = !!inp.firstTimeBuyer && netPrice > 0 && netPrice <= FTB_PRICE_CAP;
  const financeDocs = mode === 'SUBSALE' && !!inp.financeDocCosts;

  const docCostsAt = (facility) =>
    loanLegalFee(facility) + DISBURSEMENT_ESTIMATE + (ftbEligible ? 0 : loanStampDuty(facility)) + valuationFee(netPrice);

  let facility = baseLoan;
  if (financeDocs && baseLoan > 0) {
    for (let i = 0; i < 4; i++) facility = baseLoan + docCostsAt(facility);
  }

  const fees = {
    spaLegal: spaLegalFee(netPrice),
    motStamp: motStampDuty(netPrice),
    loanLegal: facility > 0 ? loanLegalFee(facility) : 0,
    loanStamp: facility > 0 ? loanStampDuty(facility) : 0,
    valuation: mode === 'SUBSALE' ? valuationFee(netPrice) : 0,
  };

  // status per fee: 'pay' (cash upfront) | 'free' (developer absorbs)
  //               | 'exempt' (Budget 2026 FTB) | 'financed' (inside the loan)
  const feeStatus = (freeToggle, exemptable, financeable) => {
    if (exemptable && ftbEligible) return 'exempt';
    if (mode === 'NEW' && freeToggle) return 'free';
    if (financeable && financeDocs) return 'financed';
    return 'pay';
  };

  const items = [
    { key: 'spaLegal', label: 'SPA legal fee', amount: fees.spaLegal, status: feeStatus(inp.freeSpaLegal, false, false) },
    // Disbursements ride with their legal fee: developer-absorbed legal work
    // absorbs its disbursements too; the subsale finance-docs option rolls the
    // loan-side disbursements into the facility along with the loan legal fee.
    { key: 'spaDisb', label: 'Disbursement & misc charges*', amount: netPrice > 0 ? DISBURSEMENT_ESTIMATE : 0, status: feeStatus(inp.freeSpaLegal, false, false) },
    { key: 'motStamp', label: 'SPA stamp duty (MOT/ DOA)', amount: fees.motStamp, status: feeStatus(inp.freeSpaStamp, true, false) },
    { key: 'loanLegal', label: 'Loan legal fee', amount: fees.loanLegal, status: feeStatus(inp.freeLoanLegal, false, true) },
    { key: 'loanDisb', label: 'Disbursement & misc charges*', amount: facility > 0 ? DISBURSEMENT_ESTIMATE : 0, status: feeStatus(inp.freeLoanLegal, false, true) },
    { key: 'loanStamp', label: 'Loan stamp duty (0.5%)', amount: fees.loanStamp, status: feeStatus(inp.freeLoanStamp, true, true) },
    ...(mode === 'SUBSALE'
      ? [{ key: 'valuation', label: 'Valuation fee', amount: fees.valuation, status: financeDocs ? 'financed' : 'pay' }]
      : []),
  ];

  const feesPayable = items.reduce((s, f) => s + (f.status === 'pay' ? f.amount : 0), 0);
  const installment = monthlyInstallment(facility, inp.ratePct, inp.years);
  const nMonths = Math.round(num(inp.years) * 12);
  const totalPayment = installment * nMonths;

  const area = num(inp.area);
  const rent = num(inp.rent);
  const utilities = num(inp.utilities);

  return {
    price,
    netPrice,
    down,
    otherFinanced,
    facility,
    financedCosts: facility - baseLoan,
    marginPct: netPrice > 0 ? (facility / netPrice) * 100 : 0,
    installment,
    totalPayment,
    totalInterest: Math.max(0, totalPayment - facility),
    items,
    entryTotal: down + feesPayable,
    ftbEligible,
    ftbBlocked: !!inp.firstTimeBuyer && netPrice > FTB_PRICE_CAP,
    pricePerArea: area > 0 ? netPrice / area : null,
    grossYieldPct: netPrice > 0 && rent > 0 ? ((rent * 12) / netPrice) * 100 : null,
    netYieldPct: netPrice > 0 && rent > 0 ? (((rent - utilities) * 12) / netPrice) * 100 : null,
    cashFlow: rent > 0 || utilities > 0 ? rent - utilities - installment : null,
  };
}

// ─── DSR ─────────────────────────────────────────────────────────────────
export const MAX_DSR_PCT = 70; // common Malaysian bank guideline
export const ELIG_RATE_PCT = 4; // assumptions for the eligible-loan estimate
export const ELIG_YEARS = 35;

// DSR = commitments / income. With "Test Loan Eligibility" on, the new
// instalment is EXCLUDED and we solve for the instalment headroom the
// allowable DSR leaves, then the loan that headroom can service.
export function computeDsr({ income, commitments, newInstalment, testEligibility }) {
  const inc = num(income);
  const commit = num(commitments) + (testEligibility ? 0 : num(newInstalment));
  const maxCommitment = (inc * MAX_DSR_PCT) / 100;
  const headroom = Math.max(0, maxCommitment - commit);
  return {
    income: inc,
    commit,
    dsrPct: inc > 0 ? (commit / inc) * 100 : 0,
    maxCommitment,
    headroom,
    eligibleLoan: maxLoanFromInstallment(headroom, ELIG_RATE_PCT, ELIG_YEARS),
  };
}

// Advanced DSR — estimate take-home pay from gross.
// EPF employee 11%; SOCSO 0.5% + EIS 0.2% of wages capped at the RM6,000
// ceiling; PCB estimated from the YA2025 resident scale with the standard
// reliefs (personal RM9k, EPF up to RM4k, SOCSO/EIS up to RM350, spouse RM4k
// when the spouse has no income) and the RM400 rebate ≤ RM35k chargeable.
export const EPF_EMPLOYEE_RATE = 0.11;
export const SOCSO_WAGE_CEILING = 6000;
export const SOCSO_EMPLOYEE_RATE = 0.005;
export const EIS_EMPLOYEE_RATE = 0.002;

const TAX_BRACKETS = [
  { upTo: 5000, rate: 0 },
  { upTo: 20000, rate: 0.01 },
  { upTo: 35000, rate: 0.03 },
  { upTo: 50000, rate: 0.06 },
  { upTo: 70000, rate: 0.11 },
  { upTo: 100000, rate: 0.19 },
  { upTo: 400000, rate: 0.25 },
  { upTo: 600000, rate: 0.26 },
  { upTo: 2000000, rate: 0.28 },
  { upTo: Infinity, rate: 0.3 },
];

export const MARITAL_OPTIONS = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MARRIED_SPOUSE_WORKING', label: 'Married (Spouse Working)' },
  { value: 'MARRIED_SPOUSE_NOT_WORKING', label: 'Married (Spouse Not Working)' },
];

export function estimateNetIncome({ grossMonthly, epfOn, marital }) {
  const gross = num(grossMonthly);
  const epf = epfOn ? gross * EPF_EMPLOYEE_RATE : 0;
  const capped = Math.min(gross, SOCSO_WAGE_CEILING);
  const socso = capped * SOCSO_EMPLOYEE_RATE;
  const eis = capped * EIS_EMPLOYEE_RATE;

  const spouseRelief = marital === 'MARRIED_SPOUSE_NOT_WORKING';
  const reliefs =
    9000 +
    Math.min(epf * 12, 4000) +
    Math.min((socso + eis) * 12, 350) +
    (spouseRelief ? 4000 : 0);
  const chargeable = Math.max(0, gross * 12 - reliefs);
  let annualTax = slice(chargeable, TAX_BRACKETS);
  if (chargeable <= 35000) annualTax = Math.max(0, annualTax - 400 - (spouseRelief ? 400 : 0));

  const tax = annualTax / 12;
  return { gross, epf, socso, eis, tax, net: gross - epf - socso - eis - tax };
}
